// React
import React, { useState, useEffect, Fragment } from 'react'
import PropTypes from 'prop-types'

// Components
import BPicker from 'ui/blocks/Picker'
import Popover from 'ui/components/Popover'
import Icon from 'ui/components/Icon'
import { Content } from './Content'
import PortalWrapper from 'ui/components/PortalWrapper'

// Hooks
import { usePicker } from 'ui/components/Picker/hooks'

// Shared
import { useTranslation } from 'shared/utils/useTranslation'

export const Timeslots = (props) => {
  const [active, setActive] = useState(false)

  const { t } = useTranslation('user')

  const picker = usePicker()

  useEffect(() => {
    if (!props.usePortal) return

    // This component has a single mode value, so we can hardcode it here
    const isActive = picker.selection === 'timeslot'

    setActive(isActive)

    props.onTrigger?.()

    if (isActive) {
      props.onOpen?.()
    } else {
      props.onHide?.()
    }
  }, [picker.selection, props.usePortal])

  const value = (props.options || []).find((option) => option.value === props.field.value)?.label
  const [from, to] = value?.split(' - ') ?? ''

  const handleSelect = (value) => {
    props.field.onChange(value)

    setActive(false)
  }

  const handleClick = (e) => {
    e.preventDefault()

    // Altering the callbacks as the active will be set to alter value
    if (active) {
      picker.setSelection(null)

      props.onHide?.()
    } else {
      picker.setSelection('timeslot')

      props.onOpen?.()
    }

    setActive((prev) => !prev)

    props.onTrigger?.()
  }

  const handleTrigger = () => {
    setActive(true)

    props.onTrigger?.()
  }

  const handleHide = () => {
    setActive(false)

    props.onHide?.()
  }

  const handleOpen = () => {
    setActive(true)

    picker.setSelection('timeslot')

    props.onOpen?.()
  }

  const handleSetActive = (e) => {
    e.preventDefault()

    setActive(true)
  }

  if (props.usePortal) {
    return (
      <Fragment>
        <BPicker.Button
          type="button"
          active={active}
          empty={!value}
          error={props.error}
          disabled={props.disabled}
          onClick={handleClick}
          data-tid={props['data-tid']}
          modifiers="width-100p"
        >
          {value && (
            <BPicker.Timeslot>
              {from}
              <Icon icon="arrow-right" />
              {to}
            </BPicker.Timeslot>
          )}
          {!value && (
            <BPicker.Timeslot>
              {t(props.translate ? 'user.store.timeslot_from' : 'common.start.present')}
              <Icon icon="arrow-right" />
              {t(props.translate ? 'user.store.timeslot_until' : 'common.stop.present')}
            </BPicker.Timeslot>
          )}
        </BPicker.Button>
        <PortalWrapper open={active} containerRef={props.portalRef}>
          <Content
            {...props}
            onSelect={handleSelect}
            showAvailability={props.showAvailability && !!props.lineCount}
            useAMOrPM={false}
            data-tid="Timeslots popover"
          />
        </PortalWrapper>
      </Fragment>
    )
  }

  return (
    <Popover
      {...(props.popoverOptions || {})}
      innerRef={(ref) => {
        props.inputRef.current = ref
      }}
      content={(
        <Content
          {...props}
          onSelect={handleSelect}
          showAvailability={props.showAvailability && !!props.lineCount}
          useAMOrPM={false}
          data-tid="Timeslots popover"
        />
      )}
      method="click"
      direction="bottom"
      offset={15}
      onTrigger={handleTrigger}
      onHide={handleHide}
      onOpen={handleOpen}
      delay={[200, null]}
    >
      <BPicker.Button
        type="button"
        active={active}
        empty={!value}
        error={props.error}
        disabled={props.disabled}
        data-tid={props['data-tid']}
        onClick={handleSetActive}
      >
        {value && (
          <BPicker.Timeslot>
            {from}
            <Icon icon="arrow-right" />
            {to}
          </BPicker.Timeslot>
        )}
        {!value && (
          <BPicker.Timeslot>
            {t(props.translate ? 'user.store.timeslot_from' : 'common.start.present')}
            <Icon icon="arrow-right" />
            {t(props.translate ? 'user.store.timeslot_until' : 'common.stop.present')}
          </BPicker.Timeslot>
        )}
      </BPicker.Button>
    </Popover>
  )
}

Timeslots.propTypes = {
  'options': PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.any,
      value: PropTypes.string
    })
  ),
  'field': PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date), PropTypes.object]),
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onInvalid: PropTypes.func,
    /** Custom validation function should return invalid key or true
     * ex. validate = (date) => 'HH'
     */
    validate: PropTypes.func
  }),
  'popoverOptions': PropTypes.object,
  'error': PropTypes.bool,
  'disabled': PropTypes.bool,
  'useAMOrPM': PropTypes.bool,
  'showAvailability': PropTypes.oneOf([true, false, 'count']),
  'lineCount': PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  'placeholder': PropTypes.any,
  'onOpen': PropTypes.func,
  'onTrigger': PropTypes.func,
  'onSelectionChange': PropTypes.func,
  'onHide': PropTypes.func,
  'data-tid': PropTypes.string,
  'inputRef': PropTypes.any,
  'portalRef': PropTypes.any,
  'inline': PropTypes.bool,
  'translate': PropTypes.bool,
  'usePortal': PropTypes.bool
}
